import { useMutation } from "@tanstack/react-query";
import {
  Modes,
  cn,
  getEnumName,
  queryClient,
  service,
  useAuthConfig,
} from "@theprelude/core";
import {
  Button,
  ButtonProps,
  TooltipArrow,
  TooltipContent,
  Tooltip,
  TooltipTrigger,
  TooltipProvider,
  notifyError,
  notifySuccess,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogBody,
  DialogClose,
  DialogTitle,
} from "@theprelude/ds";
import { usePopper } from "@/hooks/use-popper";
import { scheduleQueryOptions } from "@/queries/schedule";

export function ToggleAutopilotButton() {
  const dialog = usePopper();
  const authConfig = useAuthConfig();
  const scheduleQuery = scheduleQueryOptions(authConfig).useQuery();
  const autopilotEnabled = scheduleQuery.data?.autopilotEnabled ?? false;

  const toggleAutopilot = useMutation({
    mutationFn: async () => {
      if (!autopilotEnabled) {
        await service(authConfig).iam.updateAccount({
          mode: getEnumName(Modes, Modes.AUTOPILOT),
        });
        return true;
      }
      await service(authConfig).iam.updateAccount({
        mode: getEnumName(Modes, Modes.MANUAL),
      });
      return false;
    },
    onSuccess: async (enabled) => {
      notifySuccess({
        id: "autopilot",
        message: enabled
          ? "Autopilot has been enabled. Your schedule is being handled automatically."
          : "Autopilot has been disabled. You can now edit, delete, or create new tests manually.",
        dismissible: true,
      });
      dialog.close();
    },
    onError: async (error) => {
      notifyError({
        id: "autopilot",
        message: "Error occurred while attempting to enable autopilot",
        error,
        dismissible: true,
      });
    },
    onSettled: async () => {
      void scheduleQueryOptions(authConfig).invalidate(queryClient);
    },
  });

  return (
    <>
      <Dialog {...dialog}>
        <DialogContent className="w-96">
          <DialogHeader>
            <DialogTitle>
              Are you sure you want to enable Autopilot?
            </DialogTitle>
          </DialogHeader>
          <DialogBody>
            Autopilot selects the most relevant test to run for each endpoint,
            automatically. You can disable this mode at any time.
          </DialogBody>
          <DialogFooter>
            <DialogClose asChild>
              <Button intent="secondary">Cancel</Button>
            </DialogClose>

            <AutopilotButton
              enabled={autopilotEnabled}
              loading={toggleAutopilot.isPending}
              onClick={() => {
                toggleAutopilot.mutate();
              }}
            />
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <AutopilotButton
        enabled={autopilotEnabled}
        loading={toggleAutopilot.isPending}
        onClick={() => {
          if (autopilotEnabled) {
            toggleAutopilot.mutate();
          } else {
            dialog.on();
          }
        }}
      />
    </>
  );
}

const getButtonText = (isEnabled: boolean, isLoading: boolean) => {
  if (isLoading) {
    return isEnabled ? "Disabling..." : "Enabling...";
  }
  return isEnabled ? "Autopilot Enabled" : "Enable Autopilot";
};

interface AutopilotButtonProps extends Omit<ButtonProps, "intent"> {
  enabled?: boolean;
  hideTooltip?: boolean;
  loading?: boolean;
}

const AutopilotButton: React.FC<AutopilotButtonProps> = ({
  enabled = false,
  loading = false,
  ...buttonProps
}) => {
  return (
    <Button
      size="small"
      data-testid="schedule-autopilot"
      loading={false}
      disabled={loading}
      className={cn(
        "w-42 justify-between border border-stroke-line bg-surface-background hover:border-primary-foreground",
        enabled &&
          "border-status-green text-status-green hover:border-status-green",
      )}
      icon={
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex items-center justify-center">
                <div
                  className={cn(
                    "relative inline-flex h-4 w-8 items-center rounded-2xl border border-solid border-secondary-foreground bg-surface-background p-0.5",
                    enabled && "border-status-green bg-status-green",
                  )}
                >
                  <div
                    className={cn(
                      "absolute size-3 translate-x-0 transform rounded-full border border-secondary-foreground bg-secondary-foreground transition-transform ease-in-out",
                      enabled &&
                        "translate-x-3.5 border-surface-background bg-surface-background",
                    )}
                  ></div>
                </div>
              </div>
            </TooltipTrigger>
            <TooltipContent className="w-60">
              Autopilot works by scheduling tests on relevant endpoints based on
              various calculations.
              <TooltipArrow />
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      }
      {...buttonProps}
    >
      {getButtonText(enabled, loading)}
    </Button>
  );
};
